<template>
    <div class="flex flex-col h-full max-h-screen overflow-hidden bg-gray-100">
        <Head :title="'DEV - ' + title" v-if="isDev"/>
        <Head :title="'TEST - ' + title" v-else-if="isStaging"/>
        <Head :title="title" v-else/>

        <div class="flex flex-wrap bg-white sticky top-0 z-50 shadow-md rounded-b-2xl overflow-hidden">
            <!-- Logo -->
            <div class="relative w-1/6 px-4 flex-grow-0 flex items-center">
                <Link :href="route('dashboard')" class="relative">
                    <jet-application-mark class="block max-h-12 w-auto" />
                    <div class="inline-block absolute top-0.5 -right-6 bg-blue-dark-500 text-white text-xs font-bold py-0.5 px-1" v-if="isDev">
                        DEV
                    </div>
                    <div class="inline-block absolute top-0.5 -right-6 bg-blue-dark-500 text-white text-xs font-bold py-0.5 px-1" v-if="isStaging">
                        TEST
                    </div>
                </Link>
            </div>

            <nav class="w-5/6 border-b border-gray-100">
                <!-- Primary Navigation Menu -->
                <div class="mx-auto px-4 sm:px-6 lg:px-0">
                    <div class="flex justify-between h-16">
                        <div class="flex">
                            <!-- Navigation Links -->
                            <div class="hidden space-x-8 lg:ml-4 sm:-my-px sm:ml-10 sm:flex">
                                <template v-for="mainParentRoute in mainParentRoutes">
                                    <jet-nav-link
                                        :href="route(mainParentRoute.route)"
                                        :navRoute="mainParentRoute.route"
                                        :activeRoute="activeMainParentRoute"
                                        v-if="mainParentRoute.visible"
                                    >
                                        {{ mainParentRoute.text }}
                                    </jet-nav-link>
                                </template>
                                <a
                                    href="https://ecca.atlassian.net/servicedesk"
                                    target="_blank"
                                    class="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-bold text-blue-dark-500 leading-5 focus:outline-none hover:border-yellow-500 transition"
                                >
                                    IT Helpdesk
                                </a>
                            </div>
                        </div>

                        <!-- Hamburger -->
                        <div class="-mr-2 flex items-center sm:hidden">
                            <button @click="showingNavigationDropdown = ! showingNavigationDropdown" class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition">
                                <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                    <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                                    <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
        <div class="content-wrapper flex flex-wrap flex-auto relative">
            <div class="w-4 min-h-full absolute top-0 left-0 transform -translate-y-2/4 bg-blue-dark-700 z-0"></div>
            <!-- Sidebar -->
            <aside
                class="sidebar relative sticky flex flex-col pt-20 pb-4 overflow-y-scroll h-full bg-gradient-to-b from-blue-dark-700 from-blue-dark-700 to-blue-dark-500 text-white max-w-[260px]"
                :class="showSidebar ? 'w-1/6' : 'w-10'"
            >
                <SidebarToggle @toggleSidebar="handleSidebarToggle" :showSidebar="showSidebar" />

                <sidebar-menu
                    :subRoutes="subRoutes"
                    class="transition-opacity"
                    :class="showSidebar ? 'opacity-100 delay-500' : 'opacity-0'"
                />

                <div
                    class="relative mt-auto transition-opacity"
                    :class="showSidebar ? 'opacity-100 delay-500' : 'opacity-0'"
                >
                    <!-- Teams Dropdown -->
                    <jet-dropdown align="right" width="60" v-if="$page.props.jetstream.hasTeamFeatures">
                        <template #trigger>
                        <span class="inline-flex rounded-md">
                            <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:bg-gray-50 active:bg-gray-50 transition">
                                {{ $page.props.user.current_team.name }}

                                <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </span>
                        </template>

                        <template #content>
                            <div class="w-60">
                                <!-- Team Management -->
                                <template v-if="$page.props.jetstream.hasTeamFeatures">
                                    <div class="block px-4 py-2 text-xs text-gray-400">
                                        Manage Team
                                    </div>

                                    <!-- Team Settings -->
                                    <jet-dropdown-link :href="route('teams.show', $page.props.user.current_team)">
                                        Team Settings
                                    </jet-dropdown-link>

                                    <jet-dropdown-link :href="route('teams.create')" v-if="$page.props.jetstream.canCreateTeams">
                                        Create New Team
                                    </jet-dropdown-link>

                                    <div class="border-t border-gray-100"></div>

                                    <!-- Team Switcher -->
                                    <div class="block px-4 py-2 text-xs text-gray-400">
                                        Switch Teams
                                    </div>

                                    <template v-for="team in $page.props.user.all_teams" :key="team.id">
                                        <form @submit.prevent="switchToTeam(team)">
                                            <jet-dropdown-link as="button">
                                                <div class="flex items-center">
                                                    <svg v-if="team.id == $page.props.user.current_team_id" class="mr-2 h-5 w-5 text-green-400" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                    <div>{{ team.name }}</div>
                                                </div>
                                            </jet-dropdown-link>
                                        </form>
                                    </template>
                                </template>
                            </div>
                        </template>
                    </jet-dropdown>

                    <!-- Settings Dropup -->
                    <div class="relative">
                        <jet-dropup align="left" width="full">
                            <template #trigger>
                                <span class="inline-flex w-full" v-if="$page.props.jetstream.managesProfilePhotos" >
                                    <button type="button" class="w-full inline-flex items-center justify-center py-4 border border-transparent text-base font-semibold bg-blue-dark-700 hover:text-blue-200 focus:outline-none transition">
                                        <img class="h-8 w-8 rounded-full object-cover mr-2" :src="$page.props.user.profile_photo_url" :alt="$page.props.user.name"/>

                                        {{ $page.props.user.name }}
                                    </button>
                                </span>

                                <span class="inline-flex w-full" v-else>
                                    <button type="button" class="w-full inline-flex items-center justify-center py-4 border border-transparent text-base font-semibold bg-blue-dark-700 hover:text-blue-200 focus:outline-none transition">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1 mb-0.5 fill-current" viewBox="0 0 20 20" fill="currentColor">
                                          <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                                        </svg>

                                        {{ $page.props.user.name }}
                                    </button>
                                </span>
                            </template>

                            <template #content>
                                <!-- Account Management -->
<!--                                <div class="block px-4 py-2 text-base font-bold text-purple-dark-900 border-b-2 border-purple-dark-900">-->
<!--                                    Manage Account-->
<!--                                </div>-->

                                <jet-dropup-link :href="route('profile.show')">
                                    Profiel
                                </jet-dropup-link>

                                <jet-dropup-link :href="route('settings.show')" v-if="this.$page.props.user.roles.includes('it') || this.$page.props.user.roles.includes('admin')
                                    || this.$page.props.user.permissions.includes('settings.notifications')">
                                    Settings
                                </jet-dropup-link>

                                <jet-dropup-link :href="route('api-tokens.index')" v-if="$page.props.jetstream.hasApiFeatures">
                                    API Tokens
                                </jet-dropup-link>

                                <div class="border-t border-blue-dark-900"></div>

                                <!-- Authentication -->
                                <jet-dropup-link :href="route('impersonate.leave')" v-if="$page.props.user.impersonated">
                                    Stop impersonation
                                </jet-dropup-link>
                                <form @submit.prevent="logout" v-else>
                                    <jet-dropup-link as="button">
                                        Afmelden
                                    </jet-dropup-link>
                                </form>
                            </template>
                        </jet-dropup>
                    </div>
                </div>
            </aside>
            <div
                class="content relative h-full overflow-y-scroll border-b-4 border-yellow-500 flex-1"
                :class="showSidebar ? 'w-5/6' : 'content-full'"
            >
                <!-- Breadcrumbs -->
                <Breadcrumbs :mainParentRoutes="mainParentRoutes" :subRoutes="subRoutes" />

                <!-- Page Heading -->
                <header class="absolute bg-blue-light-500 lg:ml-4 rounded-b-xl shadow-md" v-if="$slots.header">
                    <div class="mx-auto py-3 px-3">
                        <slot name="header"></slot>
                    </div>
                </header>

                <!-- Page Content -->
                <main class="pt-20 pb-12">
                    <FlashMessages />
                    <slot name="dashboard" v-if="$slots.dashboard"></slot>

                    <!-- Default page Content -->
                    <div class="py-8 px-4 bg-white border-b border-gray-200 shadow-md w-min min-w-full" v-if="$slots.pageContent">
                        <slot name="pageContent"></slot>
                    </div>
                </main>
            </div>
        </div>
    </div>
</template>

<script>
    import JetApplicationMark from '@/Jetstream/ApplicationMark.vue'
    import JetDropdown from '@/Jetstream/Dropdown.vue'
    import JetDropdownLink from '@/Jetstream/DropdownLink.vue'
    import JetDropup from '@/Jetstream/DropUp.vue'
    import JetDropupLink from '@/Jetstream/DropupLink.vue'
    import JetNavLink from '@/Jetstream/NavLink.vue'
    import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue'
    import { Head, Link } from '@inertiajs/inertia-vue3';
    import SidebarMenu from "../Components/SidebarMenu";
    import SidebarToggle from "../Components/SidebarToggle";
    import Breadcrumbs from "../Components/Breadcrumbs";
    import FlashMessages from "@/Components/FlashMessages";

    import store from "../store";
    import { ref, watch, onMounted, onUpdated } from 'vue';

    export default {
        props: {
            title: String,
        },

        components: {
            SidebarMenu,
            Head,
            JetApplicationMark,
            JetDropdown,
            JetDropdownLink,
            JetDropup,
            JetDropupLink,
            JetNavLink,
            JetResponsiveNavLink,
            Link,
            SidebarToggle,
            Breadcrumbs,
            FlashMessages
        },
        data() {
            return {
                showSidebar: true,
                showingNavigationDropdown: false,
            }
        },
        methods: {
            switchToTeam(team) {
                this.$inertia.put(route('current-team.update'), {
                    'team_id': team.id
                }, {
                    preserveState: false
                })
            },

            logout() {
                this.$inertia.post(route('logout'));
            },

            handleSidebarToggle() {
                this.showSidebar = !this.showSidebar;
            }
        },
        setup (props) {
            const renderCount = ref(0);
            const totalPotentialDewaeleOffers = ref(0)
            const totalPendingRequests = ref(0)
            const totalFailedEmails = ref(0)

            // Increase render count on component mount
            onMounted(() => {
                renderCount.value++;
            });

            onUpdated(() => {
                renderCount.value++;
            });

            // Watch for changes in renderCount
            //We use this to set the counts next to menu items
            watch(renderCount, () => {
                axios.get('/requests/menu-data')
                    .then(response => {
                        totalPotentialDewaeleOffers.value = response.data.totalPotentialDewaeleOffers
                        totalPendingRequests.value = response.data.totalPendingRequests
                        totalFailedEmails.value = response.data.totalFailedEmails
                    })
                    .catch(error => {
                        console.error('Error fetching menu data:', error);
                    });
            });

            return {
                state: store.state,
                ...store,
                totalPotentialDewaeleOffers,
                totalPendingRequests,
                totalFailedEmails,
            };
        },
        computed: {
            mainParentRoutes() { //Routes in the main menu
                return [
                    { 'text': 'Dashboard', 'route': 'dashboard', 'visible': 1 },
                    { 'text': 'Aanvragen', 'route': 'requests', 'visible': this.$page.props.user.roles.includes('asbestos') || this.$page.props.user.roles.includes('accountancy')
                        || this.$page.props.user.roles.includes('asbestos.sampler')},
                    { 'text': 'Bestekken', 'route': 'estimates', 'visible': this.$page.props.user.permissions.includes('estimates') },
                    { 'text': 'Boekhouding', 'route': 'accountancy', 'visible': this.$page.props.user.roles.includes('accountancy') },
                    { 'text': 'Varia', 'route': 'various', 'visible': 1 },
                    { 'text': 'Beheer', 'route': 'configuration', 'visible': this.$page.props.user.permissions.includes('configuration') },
                    { 'text': 'Admin', 'route': 'admin',
                        'visible': this.$page.props.user.roles.includes('it') || this.$page.props.user.roles.includes('admin')
                    },
                ]
            },
            subRoutes() {
                return {
                    'requests': [
                        { 'text': 'Nieuwe aanvraag', 'route': 'requests.add', 'visible': (this.$page.props.user.permissions.includes('requests.add'))},
                        { 'text': 'Nieuwe aanvraag', 'route': 'requests.new', 'visible': 0 },
                        { 'text': 'Nieuwe aanvraag', 'route': 'requests.add-new', 'visible': 0 },
                        { 'text': 'Nieuwe offertes Dewaele (' + this.totalPotentialDewaeleOffers + ')', 'route': 'requests.dewaele-potential-offers', 'visible': (this.$page.props.user.permissions.includes('requests'))},
                        { 'text': 'Alle aanvragen', 'route': 'requests.all', 'visible': (this.$page.props.user.permissions.includes('requests'))},
                        { 'text': 'Te bezoeken', 'route': 'requests.to-visit', 'visible': this.$page.props.user.permissions.includes('requests') },
                        { 'text': 'Te rapporteren', 'route': 'requests.to-report', 'visible': this.$page.props.user.permissions.includes('requests') },
                        { 'text': 'Gerapporteerde aanvragen', 'route': 'requests.reported', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy')) },
                        { 'text': "Exporteer aanvragen", 'route': 'requests.export', 'visible': this.$page.props.user.permissions.includes('requests.exportall') },
                        { 'text': 'Aanvragen in wacht (' + this.totalPendingRequests + ')', 'route': 'requests.pending.index', 'visible': (this.$page.props.user.permissions.includes('requests.pending')) },
                        { 'text': 'Gefaalde e-mails (' + this.totalFailedEmails + ')', 'route': 'requests.failed-emails', 'visible': (this.$page.props.user.permissions.includes('requests')) },
                        { 'text': 'Zoeken', 'route': 'requests.search', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy')) },
                        { 'text': 'Detail aanvraag', 'route': 'requests.detail', 'visible': 0 },
                        { 'text': 'Aanvraag bewerken', 'route': 'requests.edit', 'visible': 0 },
                    ],
                    'requests.add': [

                    ],
                    'requests.all': [
                        { 'text': 'Webshop', 'url': '/requests/all/1/requests', 'target': '_self', 'route': 'requests.all.by-type', 'visible': (this.$page.props.user.permissions.includes('requests')) },
                        { 'text': 'Webshop CIB', 'url': '/requests/all/2/requests', 'target': '_self', 'route': 'requests.all.by-type', 'visible': (this.$page.props.user.permissions.includes('requests')) },
                        { 'text': 'Webshop Dewaele', 'url': '/requests/all/3/requests', 'target': '_self', 'route': 'requests.all.by-type', 'visible': (this.$page.props.user.permissions.includes('requests'))},
                        { 'text': 'Offerte', 'url': '/requests/all/4/requests', 'target': '_self', 'route': 'requests.all.by-type', 'visible': (this.$page.props.user.permissions.includes('requests'))},
                        { 'text': 'Bestek', 'url': '/requests/all/5/requests', 'target': '_self', 'route': 'requests.all.by-type', 'visible': (this.$page.props.user.permissions.includes('requests'))},
                    ],
                    'requests.reported': [
                        { 'text': 'Webshop', 'url': '/requests/reported/1/requests', 'target': '_self', 'route': 'requests.reported.by-type', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy'))},
                        { 'text': 'Webshop CIB', 'url': '/requests/reported/2/requests', 'target': '_self', 'route': 'requests.reported.by-type', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy')) },
                        { 'text': 'Webshop Dewaele', 'url': '/requests/reported/3/requests', 'target': '_self', 'route': 'requests.reported.by-type', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy')) },
                        { 'text': 'Offerte', 'url': '/requests/reported/4/requests', 'target': '_self', 'route': 'requests.reported.by-type', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy')) },
                        { 'text': 'Bestek', 'url': '/requests/reported/5/requests', 'target': '_self', 'route': 'requests.reported.by-type', 'visible': (this.$page.props.user.permissions.includes('requests') || this.$page.props.user.permissions.includes('accountancy'))},
                    ],
                    'accountancy': [
                        { 'text': 'Facturatieklanten', 'route': 'accountancy.invoice-customers', 'visible': this.$page.props.user.permissions.includes('accountancy')},
                        { 'text': 'Nafacturatie', 'route': 'accountancy.post-invoices', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': "Creditnota's", 'route': 'accountancy.credit-notes.index', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': "0-facturen", 'route': 'accountancy.zero-invoices', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                    ],
                    'accountancy.post-invoices': [
                        { 'text': 'Nieuw', 'url': '/accountancy/post-invoices/new', 'target': '_self', 'route': 'accountancy.post-invoices.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Gefactureerd', 'url': '/accountancy/post-invoices/invoiced', 'target': '_self', 'route': 'accountancy.post-invoices.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Betaald', 'url': '/accountancy/post-invoices/paid', 'target': '_self', 'route': 'accountancy.post-invoices.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Detail nafacturatie', 'route': 'accountancy.post-invoices.post-invoice', 'visible': 0 },
                        { 'text': 'Nafacturatie bewerken', 'route': 'accountancy.post-invoices.edit-post-invoice', 'visible': 0 },
                    ],
                    'accountancy.credit-notes.index': [
                        { 'text': 'Nieuw', 'url': '/accountancy/credit-notes/status/new', 'target': '_self', 'route': 'accountancy.credit-notes.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Gecrediteerd', 'url': '/accountancy/credit-notes/status/credited', 'target': '_self', 'route': 'accountancy.credit-notes.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Terugbetaald', 'url': '/accountancy/credit-notes/status/repaid', 'target': '_self', 'route': 'accountancy.credit-notes.by-status', 'visible': this.$page.props.user.permissions.includes('accountancy') },
                        { 'text': 'Detail creditnota', 'route': 'accountancy.credit-notes.show', 'visible': 0 },
                        { 'text': 'Creditnota bewerken', 'route': 'accountancy.credit-notes.edit', 'visible': 0 },
                    ],
                    'estimates': [
                        { 'text': 'Nieuw bestek', 'route': 'estimates.new', 'visible': (this.$page.props.user.permissions.includes('estimates')) ? 1 : 0 },
                        { 'text': 'Detail bestek', 'route': 'estimates.detail', 'visible': 0 },
                        { 'text': 'Bestek bewerken', 'route': 'estimates.edit', 'visible': 0 },
                    ],
                    'various': [
                        { 'text': 'Sjabloon nafacturatie downloaden', 'url': '/various/download-template-post-invoice', 'target': '_self', 'route': 'various.null', 'visible': this.$page.props.user.permissions.includes('templates.postinvoice')},
                    ],
                    'configuration': [
                        { 'text': 'Duedates', 'route': 'configuration.request_duedates', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        // { 'text': 'Redenen overdue', 'route': 'configuration.overdue_reasons', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        { 'text': 'Redenen nafacturatie', 'route': 'configuration.post_invoice_reasons', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        { 'text': 'Type opdrachten', 'route': 'configuration.assignment_types', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        { 'text': 'Webshop producten', 'route': 'configuration.webshop-products', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        { 'text': 'Gefaalde webshop orders', 'route': 'configuration.failed-webshop-orders', 'visible': this.$page.props.user.roles.includes('admin') },
                        { 'text': 'Status webhooks webshops', 'route': 'configuration.webhooks-webshops', 'visible': this.$page.props.user.roles.includes('admin') },
                        // { 'text': 'Excel migratie', 'route': 'configuration.excel-migration', 'visible': this.$page.props.user.roles.includes('admin') },
                        // { 'text': 'Excel data import', 'route': 'configuration.excel-data-import', 'visible': this.$page.props.user.roles.includes('admin') },
                    ],
                    'configuration.assignment_types': [
                        { 'text': 'Categorieën', 'route': 'configuration.assignment_types.assignment_type_categories', 'visible': this.$page.props.user.permissions.includes('configuration') },
                        { 'text': 'Opdracht opties', 'route': 'configuration.assignment_types.product_attributes', 'visible': this.$page.props.user.roles.includes('admin') },
                    ],
                    'configuration.webshop-products': [
                        { 'text': 'Product opties', 'route': 'configuration.webshop-products.product-options', 'visible': this.$page.props.user.permissions.includes('configuration') },
                    ],
                    'admin': [
                        { 'text': 'Gebruikers', 'route': 'admin.users', 'visible': 1 },
                        { 'text': 'Rollen', 'route': 'admin.roles', 'visible': this.$page.props.user.roles.includes('admin') },
                        { 'text': 'Rechten', 'route': 'admin.permissions', 'visible': this.$page.props.user.roles.includes('admin') },
                        { 'text': 'Afdelingen', 'route': 'admin.departments', 'visible': this.$page.props.user.roles.includes('admin') },
                        { 'text': 'Horizon', 'route': 'horizon.index', 'visible': this.$page.props.user.roles.includes('admin'), 'linkType': 'normal' },
                        { 'text': 'Telescope', 'route': 'telescope', 'visible': this.$page.props.user.roles.includes('admin'), 'linkType': 'normal' },
                        { 'text': 'Totem', 'route': 'totem.dashboard', 'visible': this.$page.props.user.roles.includes('admin'), 'linkType': 'normal' },
                        { 'text': 'Log viewer', 'route': 'blv.index', 'visible': this.$page.props.user.roles.includes('admin'), 'linkType': 'normal' },
                    ],
                }
            },
            activeMainParentRoute() {
                let parent = (route().current() && route().current().lastIndexOf('.') > -1) ? route().current().substr(0, route().current().indexOf('.')) : route().current();

                return parent;
            },
            isDev() {
                if (process.env.MIX_APP_ENV == 'local' || process.env.MIX_APP_ENV == 'dev') {
                    return true
                } else {
                    return false
                }
            },
            isStaging() {
                if (process.env.MIX_APP_ENV == 'test' || process.env.MIX_APP_ENV == 'staging') {
                    return true
                } else {
                    return false
                }
            },
        },
        // mounted() {
        //     console.log(this.$page.props.user)
        // }
    }
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
